class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.content =
      this.mainDetailsToggle.querySelector("summary").nextElementSibling;

    // Animation state tracking
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;

    // Removed focusout listener to allow multiple accordions to remain open independently
    // this.mainDetailsToggle.addEventListener(
    //   "focusout",
    //   this.onFocusOut.bind(this),
    // );
    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));

    // Intercept clicks to handle animation before default toggle behavior
    this.mainDetailsToggle.addEventListener("click", this.onClick.bind(this));
  }

  onClick(e) {
    // Prevent default if clicking summary (not its children that might have their own handlers)
    const summary = this.mainDetailsToggle.querySelector("summary");
    if (e.target === summary || summary.contains(e.target)) {
      e.preventDefault();
      this.toggle();
    }
  }

  toggle() {
    // Prevent multiple simultaneous animations
    if (this.isClosing || this.isExpanding) {
      return;
    }

    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.shrink();
    } else {
      this.expand();
    }
  }

  shrink() {
    this.isClosing = true;

    // Get current height before animation
    const startHeight = `${this.content.offsetHeight}px`;
    const endHeight = "0px";

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReducedMotion) {
      // Instant close for reduced motion
      this.mainDetailsToggle.removeAttribute("open");
      this.mainDetailsToggle
        .querySelector("summary")
        .setAttribute("aria-expanded", false);
      this.isClosing = false;
      return;
    }

    // Get animation duration from CSS variable
    const duration =
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--duration-default",
        ),
      ) || 200;

    // Create closing animation
    if (this.animation) {
      this.animation.cancel();
    }

    this.animation = this.content.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: duration,
        easing: "ease-out", // Smoother easing for height animations
      },
    );

    // Remove [open] attribute after animation completes
    this.animation.onfinish = () => {
      this.mainDetailsToggle.removeAttribute("open");
      this.mainDetailsToggle
        .querySelector("summary")
        .setAttribute("aria-expanded", false);
      this.animation = null;
      this.isClosing = false;
    };

    // Handle animation cancellation (e.g., rapid toggling)
    this.animation.oncancel = () => {
      this.animation = null;
      this.isClosing = false;
    };
  }

  expand() {
    this.isExpanding = true;

    // Add [open] attribute to measure content height
    this.mainDetailsToggle.setAttribute("open", "");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", true);

    // Force reflow to get accurate height measurement
    const startHeight = "0px";
    const endHeight = `${this.content.offsetHeight}px`;

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReducedMotion) {
      // Details already has [open], just reset state
      this.isExpanding = false;
      return;
    }

    // Get animation duration from CSS variable
    const duration =
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--duration-default",
        ),
      ) || 200;

    // Create opening animation
    if (this.animation) {
      this.animation.cancel();
    }

    this.animation = this.content.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: duration,
        easing: "ease-out", // Smoother easing for height animations
      },
    );

    this.animation.onfinish = () => {
      this.animation = null;
      this.isExpanding = false;
    };

    this.animation.oncancel = () => {
      this.animation = null;
      this.isExpanding = false;
    };
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    // This handler is now used for keyboard/programmatic toggles
    // Mouse clicks go through onClick -> toggle()
    // Only handle toggle if not already animating
    if (this.isClosing || this.isExpanding) {
      return;
    }
  }

  close() {
    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.shrink();
    }
  }
}

customElements.define("details-disclosure", DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector(".header-wrapper");
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (
      document.documentElement.style.getPropertyValue(
        "--header-bottom-position-desktop",
      ) !== ""
    )
      return;
    document.documentElement.style.setProperty(
      "--header-bottom-position-desktop",
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`,
    );
  }
}

customElements.define("header-menu", HeaderMenu);
